import { useQuery } from '@tanstack/react-query';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Card, Badge, LoadingSpinner } from '@/components/common';
import api, { getErrorMessage } from '@/services/api';

/**
 * The roles this platform defines, and how many people hold each.
 *
 * READ-ONLY, deliberately. Roles are a fixed set (`User.ROLE_CHOICES`) that
 * every permission class in the backend is written against, so a role invented
 * here would be one no authorization check has ever heard of. What genuinely
 * varies per broker is RIGHTS, which Broker Settings already manages through
 * EmployeeRight / POSPRight / RoleRightDefault.
 *
 * This page previously offered "Add Role" against an endpoint that did not
 * exist. `GET /users/roles/` resolved into the user DETAIL route with
 * pk="roles" and 404'd, and because the query had no error branch the failure
 * rendered as a confident "No roles found - create your first role" over a
 * backend with no Role model at all. An empty state and a broken request must
 * never look the same, which is why the error branch below is not optional
 * polish.
 */

interface Role {
  code: string;
  name: string;
  user_count: number;
}

interface RolesResponse {
  count: number;
  results: Role[];
}

export function RolesPage() {
  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<RolesResponse>({
    queryKey: ['roles'],
    queryFn: async () => {
      const res = await api.get('/users/roles/');
      return res.data;
    },
  });

  const roles = data?.results ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles"
        subtitle="The roles this platform defines. Permissions are managed per broker under Broker Settings."
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        <Card>
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="w-12 h-12 mx-auto text-red-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">
              Roles could not be loaded
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getErrorMessage(error)}</p>
          </div>
        </Card>
      ) : roles.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <ShieldCheckIcon className="w-12 h-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No roles defined</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              This would mean the platform has no roles configured at all — which should not happen.
            </p>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Users
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {roles.map((role) => (
                  <tr key={role.code} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <ShieldCheckIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{role.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-xs text-gray-500 dark:text-gray-400">{role.code}</code>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={role.user_count > 0 ? 'success' : 'default'} size="sm">
                        {role.user_count}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default RolesPage;
